import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SendtooLogo from "@/components/shared/SendtooLogo";

export default function Verify2FA() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeTwoFactorLogin } = useAuth();
  const pendingToken = location.state?.pendingToken;

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  if (!pendingToken) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-2">Session Expired</h1>
          <p className="text-slate-500 text-sm mb-6">This 2FA session is no longer valid. Please log in again.</p>
          <Link to="/auth/login" className="text-brand-500 font-semibold hover:underline">Back to Sign In</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await completeTwoFactorLogin(pendingToken, code.trim());
      toast.success(`Welcome back, ${user.name}!`);
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex justify-center">
          <SendtooLogo size="lg" align="center" />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-brand-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Two-Factor Verification</h1>
          </div>
          <p className="text-slate-500 text-sm mb-6">
            Enter the 6-digit code from your authenticator app, or one of your backup codes.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Verification Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code or backup code"
                required
                autoFocus
                data-testid="verify-2fa-code"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm text-center tracking-widest font-mono focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="verify-2fa-submit"
              className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 uppercase tracking-wide"
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6">
            <Link to="/auth/login" className="hover:text-slate-600">← Back to Sign In</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
